import { axios } from '@/plugins/axios';
import merge from 'lodash/merge';
import { Resource, Pagination, Statement } from '@/helpers/Interfaces';
import Pager from '@/helpers/Pager';
import { DateTime } from 'luxon';
import { BannerGroupTypeEnum } from '@/helpers/Enums';

/**
 * BannerGroupsService
 */
export default class BannerGroupsService
{
    /**
     * @param filter FilterModel
     * @param pager Pager
     *
     * @returns Promise<Pagination<Resource<ListItemModel>>>
     */
    public static async getList(filter: FilterModel, pager: Pager): Promise<Pagination<Resource<ListItemModel>>>
    {
        return (await axios.get<Pagination<Resource<ListItemModel>>>(`admin/cms/banners/groups`, {
            params: merge({}, filter, pager.data())
        })).data;
    }

    /**
     * @param id number
     *
     * @returns Promise<Resource<FormModel>>
     */
    public static async fetch(id: number): Promise<Resource<FormModel>>
    {
        return (await axios.get<Resource<FormModel>>(`admin/cms/banners/groups/${id}`)).data;
    }

    /**
     * @param id number
     * @param model UpsertModel
     *
     * @returns Promise<Statement>
     */
    public static async create(model: FormModel):  Promise<Resource<FormModel>>
    {
        return (await axios.post(`admin/cms/banners/groups`, model)).data as Resource<FormModel>;
    }

    /**
     * @param id number
     * @param model UpsertModel
     *
     * @returns Promise<Statement>
     */
    public static async update(id: number, model: FormModel):  Promise<Resource<FormModel>>
    {
        return (await axios.put(`admin/cms/banners/groups/${id}`, model)).data as Resource<FormModel>;
    }

    /**
     * @param id number
     *
     * @returns Promise<Statement>
     */
    public static async remove(id: number): Promise<Statement>
    {
        return (await axios.delete(`admin/cms/banners/groups/${id}`)).data as Statement;
    }

    /**
     * @param search string
     * @param limit number
     *
     * @returns Promise<ListItemModel[]>
     */
    public static async getAutocompleteList(search: string, limit: number = 10, internalOnly = false): Promise<ListItemModel[]>
    {
        return (await axios.get<ListItemModel[]>(`admin/cms/banners/groups/autocomplete`, {
            params: {
                search: search,
                limit: limit,
                internalOnly: internalOnly
            }
        })).data;
    }

    /**
    * @param id number
    *
    * @returns Promise<ListItemModel>
    */
    public static async fetchAutocompleteModel(id: number): Promise<ListItemModel[]>
    {
        return (await axios.get<ListItemModel[]>(`admin/cms/banners/groups/autocomplete/${id}`)).data;
    }
}

export interface ListItemModel
{
    id: number;
    name: string;
    type: string;
    dateCreatedUtc?: DateTime;
    dateModifiedUtc?: DateTime;
}

export interface FormModel
{
    id: number;
    name: string;
    type: string;
    contentJson: string;
}

export interface FilterModel
{
    name: string;
    type: BannerGroupTypeEnum;
}

export interface ListItemOptionsModel
{
    id: number;
    name: string;
}
